/**
 * ClinicEntryPage — enter a clinic by its 6-digit code (typed or scanned).
 *
 * Route: `/clinic-entry` (ProtectedRoute). Reached from the post-login
 * chooser ("Enter a clinic") and from the Dashboard's "change clinic"
 * button.
 *
 * The customer is physically at a clinic. They either type the 6-digit
 * code the clinic gives them, or tap "Scan QR" to read the clinic's QR.
 * Either way we resolve the code to a clinic via `clinicsApi.resolveClinic`
 * (GET /clinics/:code) and, on success, store it as the CURRENT clinic in
 * Redux and continue to the Dashboard — which then shows a "you are
 * currently at {clinic}" banner.
 *
 * Input handling mirrors the checkout clinic-code field: numeric mobile
 * keypad, Persian/Arabic digits normalized to Latin, 6-digit max, inline
 * format validation before the round-trip, and `CLINIC_NOT_FOUND` mapped
 * to a field error.
 *
 * QR: the QrScannerDialog returns a 6-digit code; we fill the field and
 * resolve immediately (no extra tap), matching the OTP auto-submit feel.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  Card,
  CardContent,
  Box,
  CircularProgress,
  MenuItem,
  ListItemText,
  Skeleton,
} from '@mui/material';
import Container from '@/components/PageSheet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LoginIcon from '@mui/icons-material/Login';
import HistoryIcon from '@mui/icons-material/History';

import LanguageToggle from '@/components/LanguageToggle';
import QrScannerDialog from '@/components/QrScannerDialog';
import { clinicsApi } from '@/services/api';
import { setCurrentClinic } from '@/store/clinicSlice';
import { selectIsRtl, selectLanguage } from '@/store/uiSlice';
import { normalizeDigits } from '@/utils/digits';
import { localized } from '@/utils/localizedContent';

const CODE_LENGTH = 6;

export function ClinicEntryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isRtl = useSelector(selectIsRtl);
  const language = useSelector(selectLanguage);

  const [code, setCode] = useState('');
  const [fieldError, setFieldError] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);

  // Clinics this customer has entered before — offered as a quick-pick so they
  // don't have to ask for the code again on a repeat visit.
  const { data: visitedData, isLoading: visitedLoading } = useQuery({
    queryKey: ['my-clinics'],
    queryFn: () => clinicsApi.listMyClinics(),
  });
  const visited = visitedData?.data?.clinics ?? [];

  const mutation = useMutation({
    mutationFn: (clinicCode) => clinicsApi.resolveClinic(clinicCode),
    onSuccess: ({ data }) => {
      const clinic = data?.clinic;
      dispatch(
        setCurrentClinic({
          code: clinic.clinicCode,
          name: clinic.clinicName,
          // Drives the dashboard's "capture my face" action.
          capturePolicy: clinic.capturePolicy || 'clinic_only',
        }),
      );
      navigate('/dashboard', { replace: true });
    },
    onError: (err) => {
      const errCode = err?.response?.data?.error?.code;
      if (errCode === 'CLINIC_NOT_FOUND') {
        setFieldError(t('clinicEntry.notFound'));
      } else if (!err?.response) {
        setFieldError(t('auth.networkError'));
      } else {
        setFieldError(t('errors.serverError'));
      }
    },
  });

  /** Validate the 6-digit format, then resolve. Shared by submit + QR. */
  const resolveCode = (raw) => {
    const normalized = normalizeDigits(raw).replace(/\D/g, '').slice(0, CODE_LENGTH);
    setFieldError(null);
    if (!/^\d{6}$/.test(normalized)) {
      setFieldError(t('clinicEntry.invalidFormat'));
      return;
    }
    mutation.mutate(normalized);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    resolveCode(code);
  };

  /** QR scanner found a code: fill the field and resolve right away. */
  const handleDetect = (scanned) => {
    setScannerOpen(false);
    setCode(scanned);
    resolveCode(scanned);
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/welcome');
  };

  const busy = mutation.isPending;

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              size="small"
              onClick={handleBack}
              aria-label={t('common.back')}
              disabled={busy}
            >
              {isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
            </IconButton>
            <StorefrontIcon color="primary" />
            <Typography variant="h6" component="h1" fontWeight={700}>
              {t('clinicEntry.title')}
            </Typography>
          </Stack>
          <LanguageToggle />
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {t('clinicEntry.subtitle')}
        </Typography>

        {/* Entry box */}
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <form onSubmit={handleSubmit} noValidate>
              <Stack spacing={2}>
                {/* Previously visited clinics — quick pick */}
                {visitedLoading ? (
                  <Skeleton variant="rounded" height={56} />
                ) : visited.length > 0 ? (
                  <>
                    <TextField
                      select
                      fullWidth
                      disabled={busy}
                      label={t('clinicEntry.previousLabel')}
                      helperText={t('clinicEntry.previousHelper')}
                      value=""
                      onChange={(e) => {
                        const picked = e.target.value;
                        setCode(picked);
                        resolveCode(picked);
                      }}
                      SelectProps={{ displayEmpty: true }}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="" disabled>
                        <Typography color="text.disabled">{t('clinicEntry.previousPlaceholder')}</Typography>
                      </MenuItem>
                      {visited.map((c) => (
                        <MenuItem key={c.clinicCode} value={c.clinicCode}>
                          <HistoryIcon fontSize="small" sx={{ marginInlineEnd: 1, color: 'text.disabled' }} />
                          <ListItemText
                            primary={localized(c.clinicName, language) || c.clinicCode}
                            secondary={<span dir="ltr">{c.clinicCode}</span>}
                          />
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Divider with "or" */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'text.disabled' }}>
                      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                      <Typography variant="caption">{t('clinicEntry.or')}</Typography>
                      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                    </Box>
                  </>
                ) : null}

                <TextField
                  label={t('clinicEntry.codeLabel')}
                  value={code}
                  onChange={(e) => {
                    const v = normalizeDigits(e.target.value)
                      .replace(/\D/g, '')
                      .slice(0, CODE_LENGTH);
                    setCode(v);
                    if (fieldError) setFieldError(null);
                  }}
                  fullWidth
                  autoFocus
                  disabled={busy}
                  error={!!fieldError}
                  helperText={fieldError ?? t('clinicEntry.codeHelper')}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  inputProps={{
                    dir: 'ltr',
                    maxLength: CODE_LENGTH,
                    pattern: '\\d{6}',
                    style: {
                      textAlign: 'center',
                      letterSpacing: '0.5em',
                      fontSize: '1.5rem',
                    },
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={busy || code.length !== CODE_LENGTH}
                  startIcon={
                    busy ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <LoginIcon />
                    )
                  }
                >
                  {busy ? t('clinicEntry.entering') : t('clinicEntry.enter')}
                </Button>

                {/* Divider with "or" */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    color: 'text.disabled',
                  }}
                >
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                  <Typography variant="caption">{t('clinicEntry.or')}</Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                </Box>

                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  disabled={busy}
                  startIcon={<QrCodeScannerIcon />}
                  onClick={() => {
                    setFieldError(null);
                    setScannerOpen(true);
                  }}
                >
                  {t('clinicEntry.scanQr')}
                </Button>
              </Stack>
            </form>
          </CardContent>
        </Card>
      </Stack>

      <QrScannerDialog
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetect={handleDetect}
      />
    </Container>
  );
}

export default ClinicEntryPage;
